import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, Home, Receipt, UserRound } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Button, Checkbox, DateTimePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { LabBenchTestPicker, splitOrderables, type LabBenchOrderable } from './LabBenchTestPicker';
import { EMPTY_INSURED_ORDER, LabInsuredOrderFields, insuredOrderBody, insuredOrderReady, type LabInsuredOrderValue } from './LabInsuredOrderFields';

const REFERRER_PHONE = /^\+?[0-9 ()-]{6,20}$/;

function localToIso(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

export function LabOrderPage() {
  const [params] = useSearchParams();
  const homeCollection = params.get('homeCollection') !== '0';
  const returnTo = safeReturnTo(params.get('returnTo'), '/lab');
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, hasModule } = useAuth();
  const [patientId, setPatientId] = useState(params.get('patientId') ?? '');
  const [priority, setPriority] = useState<'routine' | 'urgent' | 'stat'>('routine');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [chosen, setChosen] = useState<LabBenchOrderable[]>([]);
  const [billNow, setBillNow] = useState(true);
  const [exemption, setExemption] = useState<'credit' | 'staff' | 'free'>('credit');
  const [exemptionReason, setExemptionReason] = useState('');
  const [homeVisit, setHomeVisit] = useState(false);
  const [address, setAddress] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [referrerPhone, setReferrerPhone] = useState('');
  const [payerId, setPayerId] = useState('');
  const [insuredOrder, setInsuredOrder] = useState<LabInsuredOrderValue>(EMPTY_INSURED_ORDER);
  const [placedInvoice, setPlacedInvoice] = useState<string | null>(null);
  const insured = hasModule('insurance') && can('invoice.create', 'coverage.verify', 'claim.read');
  const payers = useQuery({
    queryKey: ['insurance-payers', 'lab-order', 'branch'],
    queryFn: () => get<{ id: string; code: string; name: string }[]>('/insurance/payers?availableInBranch=true'),
    enabled: insured,
    staleTime: 10 * 60_000,
  });

  const referrerPhoneBad = referrerPhone.trim() !== '' && !REFERRER_PHONE.test(referrerPhone.trim());
  const windowBackwards = Boolean(windowStart && windowEnd && new Date(windowEnd) <= new Date(windowStart));
  const exemptionReady = billNow || exemptionReason.trim().length >= 3;
  const billedToPayer = billNow && Boolean(payerId);
  const visitReady = (!homeVisit || (address.trim().length >= 5 && !windowBackwards)) && !referrerPhoneBad && exemptionReady && (!billedToPayer || insuredOrderReady(insuredOrder));

  const finish = () => {
    void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });
    navigate(returnTo, { replace: true });
  };

  const save = useMutation({
    mutationFn: () =>
      post<{ warnings?: string[]; invoiceId?: string | null }>('/lab/orders', {
        patientId,
        priority,
        clinicalNotes: clinicalNotes || undefined,
        referringDoctorName: referrerName.trim() || undefined,
        referringDoctorPhone: referrerPhone.trim() || undefined,
        ...splitOrderables(chosen),
        createInvoice: billNow,
        ...(billedToPayer ? { payerId, ...insuredOrderBody(insuredOrder) } : {}),
        ...(billNow ? {} : { billingExemption: exemption, exemptionReason: exemptionReason.trim() }),
        ...(homeVisit
          ? {
              homeCollection: {
                address: address.trim(),
                windowStart: localToIso(windowStart),
                windowEnd: localToIso(windowEnd),
                notes: visitNotes.trim() || undefined,
              },
            }
          : {}),
      }),
    onSuccess: (placed) => {
      toast.success(
        tr('lab', 'order_placed'),
        `${chosen.length} item(s) sent to the bench${billNow ? ' and invoiced' : ''}${homeVisit ? ' · home collection requested' : ''}`,
      );
      if (placed.warnings && placed.warnings.length > 0) toast.warning(tr('lab', 'check_the_bill'), placed.warnings.join(' '));
      if (placed.invoiceId && !payerId && can('payment.collect')) {
        setPlacedInvoice(placed.invoiceId);
        return;
      }
      finish();
    },
    onError: (error) => toast.error(tr('lab', 'could_not_place_the_order'), describeError(error)),
  });

  const ready = Boolean(patientId) && chosen.length > 0 && visitReady;
  const dirty = !save.isSuccess && (Boolean(patientId) || chosen.length > 0 || clinicalNotes !== '' || referrerName !== '' || address !== '');

  return (
    <FormScreen
      title={tr('lab', 'order_lab_tests')}
      eyebrow={tr('lab', 'order')}
      icon={FlaskConical}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title={tr('lab', 'patient')} icon={UserRound}>
        <Field label={tr('lab', 'patient')} required hint={can('patient.register') ? tr('lab', 'not_registered_yet_register_them_in_a_new_tab') : undefined} className="sm:col-span-2">
          <div className="flex gap-2">
            <div className="flex-1">
              <PatientPicker value={patientId} onChange={setPatientId} />
            </div>
            {can('patient.register') && (
              <Button type="button" variant="outline" onClick={() => window.open('/patients/new', '_blank', 'noopener')}>
                {tr('lab', 'new_patient')}
              </Button>
            )}
          </div>
        </Field>
        <Field label={tr('lab', 'priority')} htmlFor="lab-order-priority">
          <ChoiceButtons<typeof priority>
            id="lab-order-priority"
            value={priority}
            onChange={setPriority}
            inline
            options={(['routine', 'urgent', 'stat'] as const).map((p) => ({ value: p, label: humanize(p) }))}
          />
        </Field>
        <Field label={tr('lab', 'referring_doctor')} htmlFor="lab-order-referrer" hint={tr('lab', 'optional_searchable_on_the_worklist')}>
          <Input id="lab-order-referrer" maxLength={160} value={referrerName} onChange={(e) => setReferrerName(e.target.value)} />
        </Field>
        <Field
          label={tr('lab', 'referring_doctors_phone')}
          htmlFor="lab-order-referrer-phone"
          hint={tr('lab', 'optional_told_when_a_result_is_critical_or')}
          error={referrerPhoneBad ? 'Enter a valid phone number' : undefined}
        >
          <PhoneText id="lab-order-referrer-phone" inputMode="tel" maxLength={20} value={referrerPhone} onChange={(e) => setReferrerPhone(e.target.value)} />
        </Field>
        <Field label={tr('lab', 'clinical_notes')} htmlFor="lab-order-notes">
          <Textarea id="lab-order-notes" rows={2} value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('lab', 'tests')} icon={FlaskConical} columns={1}>
        <LabBenchTestPicker chosen={chosen} onChange={setChosen} inputId="lab-order-tests" />
      </FormSection>

      {hasModule('billing') && can('lab.order.unbilled') && (
        <FormSection title={tr('lab', 'bill_to')} icon={Receipt} columns={1}>
          <Checkbox checked={billNow} onCheckedChange={setBillNow} label={tr('lab', 'bill_now_at_the_desk_walk_in_patient')} />
          {billNow && insured && (
            <Field label={tr('lab', 'bill_to')} htmlFor="lab-order-payer" hint={tr('lab', 'leave_on_self_pay_unless_an_insurer_or_panel')}>
              <Select id="lab-order-payer" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
                <option value="">{tr('lab', 'self_pay')}</option>
                {(payers.data ?? []).map((payer) => (
                  <option key={payer.id} value={payer.id}>
                    {payer.code} — {payer.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {billedToPayer && <LabInsuredOrderFields value={insuredOrder} onChange={setInsuredOrder} />}
          {!billNow && (
            <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
              <Field label={tr('lab', 'covered_as')} htmlFor="lab-order-exemption" required hint={tr('lab', 'an_order_without_a_bill_is_marked_and_reported')}>
                <ChoiceButtons<typeof exemption>
                  id="lab-order-exemption"
                  value={exemption}
                  onChange={setExemption}
                  required
                  options={[
                    { value: 'credit', label: tr('lab', 'credit_billed_later') },
                    { value: 'staff', label: tr('lab', 'hospital_staff') },
                    { value: 'free', label: tr('lab', 'free_of_charge') },
                  ]}
                />
              </Field>
              <Field label={tr('lab', 'reason')} htmlFor="lab-order-exemption-reason" required>
                <Input id="lab-order-exemption-reason" maxLength={300} value={exemptionReason} onChange={(e) => setExemptionReason(e.target.value)} />
              </Field>
            </div>
          )}
        </FormSection>
      )}

      {homeCollection && (
        <FormSection title={tr('lab', 'home_collection')} icon={Home} columns={1}>
          <Checkbox checked={homeVisit} onCheckedChange={setHomeVisit} label={tr('lab', 'home_collection_a_phlebotomist_visits_the')} />
          {homeVisit && (
            <div className="space-y-3 rounded-md border p-3">
              <Field
                label={tr('lab', 'address')}
                htmlFor="lab-home-address"
                required
                error={address.length > 0 && address.trim().length < 5 ? 'Enter the full address, at least 5 characters' : undefined}
              >
                <Textarea id="lab-home-address" rows={2} maxLength={400} value={address} onChange={(e) => setAddress(e.target.value)} />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={tr('lab', 'window_starts')} htmlFor="lab-home-start">
                  <DateTimePicker id="lab-home-start" value={windowStart} onChange={setWindowStart} />
                </Field>
                <Field label={tr('lab', 'window_ends')} htmlFor="lab-home-end" error={windowBackwards ? 'Must be after the start' : undefined}>
                  <DateTimePicker id="lab-home-end" value={windowEnd} onChange={setWindowEnd} />
                </Field>
              </div>
              <Field label={tr('lab', 'visit_notes')} htmlFor="lab-home-notes" hint={tr('lab', 'gate_code_landmark_who_to_ask_for')}>
                <Input id="lab-home-notes" maxLength={400} value={visitNotes} onChange={(e) => setVisitNotes(e.target.value)} />
              </Field>
            </div>
          )}
        </FormSection>
      )}

      <FormActions
        submitLabel={`${tr('lab', 'place_order')} ${chosen.length} ${tr('lab', 'item')}${chosen.length === 1 ? '' : 's'}`}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />

      {placedInvoice && (
        <Dialog
          open
          onClose={finish}
          title={tr('lab', 'order_placed')}
          description={tr('lab', 'take_the_payment_now_so_the_specimen_can_be')}
          footer={
            <>
              <Button type="button" variant="ghost" onClick={finish}>
                {tr('lab', 'later')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  window.open(`/billing/invoices/${placedInvoice}`, '_blank', 'noopener');
                  finish();
                }}
              >
                {tr('lab', 'take_payment')}
              </Button>
            </>
          }
        >
          <p className="text-sm">{tr('lab', 'the_bill_opens_in_a_new_tab_with_its_payment')}</p>
        </Dialog>
      )}
    </FormScreen>
  );
}
